import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { connect, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Button from '~components/ui/Button'
import { authenticate, setAccount, setSuspended } from '~redux/auth'
import RoundedIcon from '~components/ui/RoundedIcon'
import Sprite from '~components/Sprite'
import Link from '../../ui/Link'
import ButtonLink from '../../ui/ButtonLink'
import { pageView } from '~helpers/ga'
import { loadCart } from '~redux/shop'

const Login = ({ isAuthenticated, suspendStatus }) => {
  const [busy, setBusy] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [bottomLinks, setBottomLinks] = useState([])
  const [errorMessage, setErrorMessage] = useState('')
  const [text, setText] = useState([])
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const copyrightDate = new Date().getFullYear()

  useEffect(() => {
    pageView()

    axios.get('/options', {
      params: { option: 'footer' }
    }).then((response) => {
      setBottomLinks(response.data.fields.bottom_links)
    })

    axios.get('/options', {
      params: { option: 'login_text' }
    }).then((response) => {
      setText(response.data.fields)
    })

    document.body.classList.add('login-body')
    return () => document.body.classList.remove('login-body')
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/')
    }
  }, [isAuthenticated, navigate])

  const handleInputChange = (event) => {
    const { name, value } = event.target
    if (name === 'email') {
      setEmail(value)
    } else if (name === 'password') {
      setPassword(value)
    }
  }

  const handleSubmit = () => {
    if (busy) {
      return
    }

    setBusy(true)

    axios.post('/login', {
      email,
      password
    }).then((response) => {
      axios.get('/api/account').then((response) => {
        dispatch(setAccount(response.data))
        dispatch(authenticate())

        if (suspendStatus === window.Panache?.suspendStatus) {
          dispatch(setSuspended())
        }

        dispatch(loadCart())
        navigate('/')
      })
    }).catch((e) => {
      setErrorMessage(e.response.data.errors.email)
    }).finally(() => {
      setBusy(false)
    })
  }

  return (
    <div className="max-w-screen-xl mx-auto md:px-8 xxl:px-0 login-container">
      <div className="flex flex-col items-start justify-between gap-10 mx-auto lg:flex-row">
        <section className="w-full px-4 py-8 md:p-10 xl:p-20 bg-lightGrey">
          <a href="/" className="z-40 block w-17 h-10 md:w-22.5 md:h-13.5 transition-opacity duration-150 hover:opacity-75 focus:opacity-75 ">
            <Sprite name="logo" />
          </a>

          <h2 className="text-left mt-15 text-md">
            {text.title}
          </h2>

          {errorMessage && (
            <div className="p-4 mt-4 border rounded-md border-error">
              <span className="block float-left w-6 h-6 mr-2 icon">
                <Sprite name="errorBubble" />
              </span>

              <div className="ml-10">
                <strong>
                  {text.error}
                </strong>

                <span className="block">
                  {errorMessage}
                </span>

                <br />

                <strong>
                  Please note
                </strong>

                <span className="block">
                  Users of the old Panache Hub need to re-register on the new system, if you have not done so already you can re-register
                  {' '}
                  <Link href="/account/register" className="underline hover:no-underline">
                    here
                  </Link>
                  .
                </span>
              </div>
            </div>
          )}

          <form className="mt-4" action="#" onSubmit={(e) => e.preventDefault() || handleSubmit()}>
            <div className="w-full mt-3 xl:w-3/4">
              <label className="inline-block mt-4">
                Email
              </label>

              <input aria-label="Email address" name="email" type="email" required value={email} onChange={handleInputChange} className="relative block w-full p-4 mt-2 rounded-md appearance-none placeholder-lightCharcoal text-charcoal focus:outline-none focus:z-10" placeholder="Email" />
            </div>

            <div className="w-full mt-3 xl:w-3/4">
              <label className="inline-block mt-4">
                Password
              </label>

              <input aria-label="Password" name="password" type="password" required value={password} onChange={handleInputChange} className="relative block w-full p-4 mt-2 rounded-md appearance-none placeholder-lightCharcoal text-charcoal focus:outline-none focus:z-10" placeholder="Password" />
            </div>

            <div className="mt-2">
              <div className="leading-5">
                <Link href="/account/forgot" className="inline-block mt-2 underline hover:no-underline">
                  {text.forgot}
                </Link>
              </div>
            </div>

            <div className="mt-6">
              <Button type="submit" className="w-auto mt-4" disabled={busy}>
                Log in
              </Button>
            </div>
          </form>
        </section>

        <aside className="w-full p-8 md:max-w-105 xl:max-w-screen-sm lg:mt-8 xl:mt-20 bg-lightGrey">
          <span className="block">
            Existing Customers
          </span>

          <h3 className="mt-2 text-left text-md">
            Register/Re-register Now
          </h3>

          <p className="mt-2">
            If you had an account on the previous version of Panache Hub you will need to re-register using the link below to access the new and improved Panache Hub as your previous login credentials will no longer work.
          </p>

          <ButtonLink
            theme="secondary"
            href="/account/register"
            className="w-auto mt-4"
          >
            Register Now
          </ButtonLink>
        </aside>
      </div>

      <div className="flex flex-wrap w-full pb-5 mx-auto mt-10 lg:mt-0 md:flex-col-reverse md:border-coral md:border-t md:pt-4 lg:flex-row-reverse lg:flex-no-wrap lg:items-start">
        <a href="mailto:info@panache-lingerie.com" className="inline-flex items-center flex-grow-0 flex-shrink-0 mx-auto mb-10 group md:mt-4 md:mx-0 md:mb-0 lg:mt-0 lg:ml-4">
          <RoundedIcon name="label" className="inline-block mr-2 w-7 h-7" />
          Need help?
        </a>

        <ul className="flex-wrap w-full -mb-3 text-center md:w-auto md:inline-flex md:text-left md:-mb-1 lg:mr-auto">
          <li className="hidden mb-3 md:mb-1 md:mr-3 md:block">
            &copy; Panache
            {' '}
            {copyrightDate}
          </li>

          {bottomLinks.map((link) => (
            <li key={link.key} className="block mb-3 md:mb-1 md:mr-3">
              <Link href={link.attributes.link} className="hover:underline">
                {link.attributes.label}
              </Link>
            </li>
          ))}

          <li className="block mb-3 md:mb-1 md:mr-3 md:hidden">
            &copy; Panache
            {' '}
            {copyrightDate}
          </li>
        </ul>
      </div>
    </div>
  )
}

Login.propTypes = {
  isAuthenticated: PropTypes.bool,
  account: PropTypes.object,
  suspendStatus: PropTypes.string
}

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  account: state.auth.account,
  suspendStatus: state.auth.account?.stockist?.status
})

export default connect(mapStateToProps)(Login)
